import React, { useState, useEffect } from 'react';
import { Truck, AlertTriangle, Wrench, ChevronDown, ChevronUp, Loader, Calendar, IdCard } from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERIDADE_COLOR = {
  critico: 'bg-red-500/15 border-red-500/40 text-red-300',
  alto: 'bg-orange-500/15 border-orange-500/40 text-orange-300',
  medio: 'bg-yellow-500/15 border-yellow-500/40 text-yellow-300',
};

const SEVERIDADE_LABEL = {
  critico: 'Crítico',
  alto: 'Alto',
  medio: 'Médio',
};

export default function AlertasVeiculosPanel({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const fetchAlertas = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/inventario/alertas-veiculos`, {
        headers: authHeaders(), cache: 'no-store',
      });
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlertas(); }, [refreshKey]);

  if (!data) {
    return loading ? (
      <div className="bg-[#111] border border-gray-800 rounded-xl p-4 flex items-center gap-2 text-gray-400 text-sm">
        <Loader size={14} className="animate-spin" /> Carregando alertas de veículos...
      </div>
    ) : null;
  }

  const { licenciamento, revisao, cnh = [], configuracao, total } = data;
  if (total === 0) {
    return (
      <div className="bg-[#0d0d0d] border border-gray-800 rounded-xl p-4 text-sm text-gray-500 flex items-center gap-2" data-testid="alertas-veiculos-empty">
        <Truck size={16} className="text-gray-600" />
        Sem alertas pendentes nos veículos da frota.
      </div>
    );
  }

  return (
    <div className="bg-[#0d0d0d] border border-blue-500/20 rounded-xl overflow-hidden" data-testid="alertas-veiculos-panel">
      <button
        onClick={() => setExpanded((s) => !s)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#111] transition-colors"
        data-testid="alertas-veiculos-toggle"
      >
        <div className="flex items-center gap-2">
          <Truck size={18} className="text-blue-400" />
          <span className="font-semibold text-white text-sm sm:text-base">Alertas de Veículos</span>
          <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/15 border border-red-500/30 text-red-300">
            {total} pendente{total === 1 ? '' : 's'}
          </span>
        </div>
        {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-gray-800">
          {/* Licenciamento */}
          <div data-testid="alertas-licenciamento-block">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={14} className="text-orange-400" />
              <h4 className="text-sm font-semibold text-orange-300">
                Licenciamento ({licenciamento.length})
              </h4>
              <span className="text-[11px] text-gray-500">
                ≤ {configuracao.dias_alerta_licenciamento} dias
              </span>
            </div>
            {licenciamento.length === 0 ? (
              <div className="text-xs text-gray-500 italic">Nenhum vencimento próximo</div>
            ) : (
              <ul className="space-y-2">
                {licenciamento.map((a) => (
                  <li
                    key={a.ativo_id}
                    data-testid={`licenciamento-item-${a.ativo_id}`}
                    className={`px-3 py-2 rounded-lg border text-xs ${SEVERIDADE_COLOR[a.severidade] || ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">{a.nome}</div>
                      <span className="px-1.5 py-0.5 rounded text-[10px] uppercase bg-black/30">
                        {SEVERIDADE_LABEL[a.severidade]}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-300 mt-1 flex items-center gap-2 flex-wrap">
                      {a.placa && <span className="font-mono bg-black/30 px-1 rounded">{a.placa}</span>}
                      {a.subtipo_label && <span>· {a.subtipo_label}</span>}
                      <span>·{' '}
                        {a.vencido ? (
                          <span className="text-red-300 font-semibold">
                            Vencido há {Math.abs(a.dias_para_vencer)} dia{Math.abs(a.dias_para_vencer) === 1 ? '' : 's'}
                          </span>
                        ) : (
                          <span>Vence em {a.dias_para_vencer} dia{a.dias_para_vencer === 1 ? '' : 's'}</span>
                        )}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Revisão */}
          <div data-testid="alertas-revisao-block">
            <div className="flex items-center gap-2 mb-2">
              <Wrench size={14} className="text-yellow-400" />
              <h4 className="text-sm font-semibold text-yellow-300">
                Revisão por km ({revisao.length})
              </h4>
              <span className="text-[11px] text-gray-500">
                ≤ {configuracao.km_alerta_revisao} km
              </span>
            </div>
            {revisao.length === 0 ? (
              <div className="text-xs text-gray-500 italic">Sem revisões próximas</div>
            ) : (
              <ul className="space-y-2">
                {revisao.map((a) => (
                  <li
                    key={a.ativo_id}
                    data-testid={`revisao-item-${a.ativo_id}`}
                    className={`px-3 py-2 rounded-lg border text-xs ${SEVERIDADE_COLOR[a.severidade] || ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">{a.nome}</div>
                      <span className="px-1.5 py-0.5 rounded text-[10px] uppercase bg-black/30">
                        {SEVERIDADE_LABEL[a.severidade]}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-300 mt-1 flex items-center gap-2 flex-wrap">
                      {a.placa && <span className="font-mono bg-black/30 px-1 rounded">{a.placa}</span>}
                      <span>· {a.km_atual?.toLocaleString('pt-BR')} km</span>
                      <span>·{' '}
                        {a.vencido ? (
                          <span className="text-red-300 font-semibold">
                            {Math.abs(a.km_ate_proxima_revisao).toLocaleString('pt-BR')} km atrasado
                          </span>
                        ) : (
                          <span>{a.km_ate_proxima_revisao.toLocaleString('pt-BR')} km para a próxima</span>
                        )}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* CNH dos motoristas */}
          <div data-testid="alertas-cnh-block">
            <div className="flex items-center gap-2 mb-2">
              <IdCard size={14} className="text-purple-400" />
              <h4 className="text-sm font-semibold text-purple-300">
                CNH ({cnh.length})
              </h4>
              <span className="text-[11px] text-gray-500">
                ≤ {configuracao.dias_alerta_cnh || 30} dias
              </span>
            </div>
            {cnh.length === 0 ? (
              <div className="text-xs text-gray-500 italic">Nenhuma CNH próxima do vencimento</div>
            ) : (
              <ul className="space-y-2">
                {cnh.map((c) => (
                  <li
                    key={c.colaborador_id}
                    data-testid={`cnh-item-${c.colaborador_id}`}
                    className={`px-3 py-2 rounded-lg border text-xs ${SEVERIDADE_COLOR[c.severidade] || ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">{c.nome}</div>
                      <span className="px-1.5 py-0.5 rounded text-[10px] uppercase bg-black/30">
                        {SEVERIDADE_LABEL[c.severidade]}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-300 mt-1 flex items-center gap-2 flex-wrap">
                      {c.cnh_categoria && <span className="font-mono bg-black/30 px-1 rounded">CNH {c.cnh_categoria}</span>}
                      {c.cnh_numero && <span className="text-gray-500">#{c.cnh_numero}</span>}
                      <span>·{' '}
                        {c.vencida ? (
                          <span className="text-red-300 font-semibold">
                            Vencida há {Math.abs(c.dias_para_vencer)} dia{Math.abs(c.dias_para_vencer) === 1 ? '' : 's'}
                          </span>
                        ) : (
                          <span>Vence em {c.dias_para_vencer} dia{c.dias_para_vencer === 1 ? '' : 's'}</span>
                        )}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="bg-blue-500/5 border-t border-blue-500/10 px-4 py-2 text-[11px] text-gray-500 flex items-center gap-2">
        <AlertTriangle size={11} className="text-blue-300" />
        Painel separado dos alertas de máquinas operacionais. Atualizado em tempo real ao editar veículos ou registrar hodômetro.
      </div>
    </div>
  );
}
